import React, { useState, useEffect, forwardRef } from "react";
import PropTypes from "kf-proptypes/widgets";
import { uniqBy } from "lodash-es";

import { useUserStore } from "base/stores";

import {
  DROPDOWN_SIZES,
  DROPDOWN_POSITION,
  DROPDOWN_TYPE,
  DROPDOWN_CHIP_SIZE_MAPPING
} from "../dropdown/constants.js";
import {
  MultiSelectDropdown,
  MultiSelectDropdownItem
} from "../dropdown/multiselect.jsx";
import { UserMenuItem } from "@widgets/datadisplay/usermenuitem/index.jsx";
import { UserChip } from "@widgets/datadisplay/userchip/index.jsx";

export function MultiUserSelectComponent(props, ref) {
  const {
    dataSource,
    defaultValue,
    value,
    size = DROPDOWN_SIZES.BASE,
    position,
    onChange,
    className,
    onRemove,
    onSearch,
    onSearchDebounced,
    onOpen,
    onClose,
    onFocus,
    autoComplete,
    isOpen,
    type = DROPDOWN_TYPE.NORMAL,
    menuItemSelectedIcon,
    dropdownClassName,
    checkDataSourceForValue = true,
    readOnly = false,
    disabled = false,
    showGroup = false,
    hint,
    errors = [],
    ...restProps
  } = props;

  const [currentDatasource, setCurrentDatasource] = useState([]);

  useEffect(function updateDataSource() {
    if (!dataSource) {
      useUserStore
        .getState()
        .getUsersById(value)
        .then((users) => {
          const selectedUsers = users;
          useUserStore
            .getState()
            .getUsers("", 1, showGroup)
            .then((users) => {
              const uniqUsers = uniqBy(
                [...selectedUsers, ...users],
                // eslint-disable-next-line max-nested-callbacks
                (user) => user._id
              );
              setCurrentDatasource(uniqUsers);
            });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const defaultSearch = ({ search }) => {
    useUserStore
      .getState()
      .getUsers(search, 1, showGroup)
      .then((users) => {
        setCurrentDatasource(users);
      });
  };

  return (
    <MultiSelectDropdown
      dataSource={dataSource || currentDatasource}
      defaultValue={defaultValue}
      value={value}
      position={position}
      className={`${className}`}
      onChange={onChange}
      onRemove={onRemove}
      onFocus={onFocus}
      size={size}
      onOpen={onOpen}
      onClose={onClose}
      autoComplete={autoComplete}
      tagRenderer={({ value: user, onClose }) => {
        return (
          <UserChip
            size={DROPDOWN_CHIP_SIZE_MAPPING[size]}
            userId={user._id}
            name={user.Name}
            onClose={!readOnly && !disabled && onClose}
            disabled={disabled}
          />
        );
      }}
      menuItemSelectedIcon={menuItemSelectedIcon}
      dropdownClassName={dropdownClassName}
      itemRenderer={(user, index) => {
        return (
          <MultiSelectDropdownItem value={user._id} label={user.Name}>
            <UserMenuItem
              userId={user._id}
              name={user.Name}
              email={user.Email}
              kind={user.Kind}
            />
          </MultiSelectDropdownItem>
        );
      }}
      onSearch={onSearch}
      onSearchDebounced={onSearchDebounced || defaultSearch}
      isOpen={isOpen}
      type={type}
      checkDataSourceForValue={checkDataSourceForValue}
      readOnly={readOnly}
      disabled={disabled}
      errors={errors}
      hint={hint}
      {...restProps}
      ref={ref}
    />
  );
}

MultiUserSelectComponent.propTypes = {
  dataSource: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      Kind: PropTypes.string,
      Email: PropTypes.string,
      FirstName: PropTypes.string,
      Name: PropTypes.string,
      Groups: PropTypes.arrayOf(
        PropTypes.shape({
          _id: PropTypes.string
        })
      )
    })
  ),
  defaultValue: PropTypes.string,
  value: PropTypes.string,
  position: PropTypes.string,
  onChange: PropTypes.func,
  className: PropTypes.string,
  onSearch: PropTypes.func,
  onSearchDebounced: PropTypes.func,
  onRemove: PropTypes.func,
  onOpen: PropTypes.func,
  size: PropTypes.string,
  onClose: PropTypes.func,
  onFocus: PropTypes.func,
  isOpen: PropTypes.bool,
  type: PropTypes.string,
  menuItemSelectedIcon: PropTypes.node,
  dropdownClassName: PropTypes.string,
  hint: PropTypes.string,
  autoComplete: PropTypes.string,
  errors: PropTypes.arrayOf(PropTypes.string),
  checkDataSourceForValue: PropTypes.bool,
  readOnly: PropTypes.bool,
  disabled: PropTypes.bool,
  showGroup: PropTypes.bool
};

export { DROPDOWN_POSITION, DROPDOWN_TYPE };

export const MultiUserSelect = forwardRef(MultiUserSelectComponent);
